/* Lavender Farm Simulator — workspace start-up fix (Greenhouse, Flower Farm, Apiary).

   The bug: each workspace module only starts when the player navigates to its route
   (the kernel sets data-canonical-route and fires lfg:route-enter). But:
   - the Greenhouse tab refuses navigation until greenhouseV38.houses has an entry, and that list is only
     filled by the Greenhouse module itself (it syncs owned map tiles / Supply Yard kits on start);
   - placing a Flower Farm on the Farm Map needs the Flower Farm module's API, which also only exists after
     navigating there, while the Flower Farm tab needs that map placement first.
   Both are chicken-and-egg deadlocks, so owned greenhouses never open and Flower Farms can't be placed.

   The fix: once the simulator and its shared bundle are ready, start those modules quietly in the
   background exactly the way the kernel does (same route attribute + lfg:route-enter), then put the route
   back. Each module has its own run-once guard, and the Greenhouse module's own normalize() then records
   any greenhouse the farm already owns. No save data is written by this file itself. */
(function () {
  'use strict';
  if (window.__gllfSimFixes) return;
  window.__gllfSimFixes = true;

  var MODULES = [
    { route: 'greenhouse', api: '_lfgGreenhouse', ready: 'greenhouseV38Ready' },
    { route: 'cutflower', api: '_lfgCutFlowerV70', ready: 'cutFlowerV70Ready' },
    { route: 'apiary', api: '_lfgApiaryV38', ready: null }
  ];


  /* Bug: in some start-ups (seen in Admin QA) the workspace <section>s lose their ids, so every tab's
     aria-controls="lfg-view-…" points at nothing (breaks screen-reader tab/panel pairing and any code that
     looks views up by id). Restore the id from the section's own data-view. */
  function restoreViewIds(root) {
    root.querySelectorAll('section.lfg-view[data-view]').forEach(function (sec) {
      if (sec.id) return;
      var id = 'lfg-view-' + sec.getAttribute('data-view');
      if (!document.getElementById(id)) sec.id = id;
    });
  }

  /* Bug: every Farm Map tile reads 64% drainage / pH 7. The site profile keeps 30 placeholder tiles
     ({ mapped: false, drainage: 64, ph: 7 }) and the soil engine uses them instead of each tile's terrain,
     so no tile reaches the 70% Lavender Row minimum and players cannot add rows (a Soil test also just
     records 64%). The map itself shows the terrain values (Sandy 90%, Loam 72%…), so the rule and the
     picture disagree. Repair: placeholder tiles (mapped === false) take their terrain's standard values;
     real mapped/surveyed tiles are never touched. Runs once per save and saves through the game. */
  var PH = { sandy: 6.6, loam: 7, clay: 7.4, slope: 6.9, wet: 6.8, windy: 6.9 };
  var DR = { sandy: 90, loam: 72, clay: 42, slope: 82, wet: 34, windy: 70 };
  function repairSoil(root) {
    var A = root._lfg, st; try { st = A.state(); } catch (e) { return; }
    var tiles = st && st.site && st.site.tiles, cells = st && st.world && st.world.cells;
    if (!Array.isArray(tiles) || !Array.isArray(cells)) return;
    var n = 0;
    tiles.forEach(function (tile, i) {
      var cell = cells[i];
      if (!tile || tile.mapped !== false || !cell) return;
      var d = DR[cell.terrain], p = PH[cell.terrain];
      if (d == null || (tile.drainage === d && tile.ph === p)) return;
      var od = tile.drainage, op = tile.ph;
      tile.drainage = d; tile.ph = p;
      if (cell.baseDrainage == null || cell.baseDrainage === od) cell.baseDrainage = d;
      if (cell.basePh == null || cell.basePh === op) cell.basePh = p;
      cell.drainage = Math.round(Math.max(20, Math.min(100, cell.baseDrainage + (+cell.drainageDelta || 0))));
      cell.ph = Math.round(Math.max(4.5, Math.min(8.8, cell.basePh + (+cell.phDelta || 0))) * 10) / 10;
      n++;
    });
    if (!n) return;
    try { A.save(); } catch (e) {}
    try { if (root._lfgWorld && root._lfgWorld.render) root._lfgWorld.render(); } catch (e) {}
    root.setAttribute('data-gllf-soil-repaired', String(n));
  }


  /* Bug: when rows are already on the Farm Map but nothing is planted yet, the field guide still says
     "establish the first row". Keep the game's guide, but make the wording match the farm. */
  var OLD_T = 'The field is ready for a first decision';
  function fixGuide(root) {
    var copy = root.querySelector('.lfg-v60-guide-copy'); if (!copy) return;
    var b = copy.querySelector('b'), p = copy.querySelector('p');
    if (!b || b.textContent.trim() !== OLD_T) return;
    var st; try { st = root._lfg.state(); } catch (e) { return; }
    var cells = st && st.world && st.world.cells;
    if (!Array.isArray(cells) || !cells.some(function (c) { return c && c.type === 'row'; })) return;
    b.textContent = 'Your lavender rows are ready to plant';
    if (p) p.textContent = 'Choose a zone-suitable cultivar and plant the first plot in the Lavender Field.';
  }
  function watchGuide(root) {
    fixGuide(root);
    new MutationObserver(function () { fixGuide(root); }).observe(root, { childList: true, subtree: true, characterData: true });
  }

  function prime(root) {
    var prev = root.dataset.canonicalRoute || 'field';
    var started = [];
    MODULES.forEach(function (m) {
      if (root[m.api] || (m.ready && root.dataset[m.ready])) return;
      if (prev === m.route) return;
      try {
        root.dataset.canonicalRoute = m.route;
        root.dispatchEvent(new CustomEvent('lfg:route-enter', { detail: Object.freeze({ route: m.route, previous: prev, primed: true }) }));
        started.push(m.route);
      } catch (e) { /* a module that fails to start keeps its original behavior */ }
    });
    root.dataset.canonicalRoute = prev;
    /* Let the Greenhouse module record greenhouses the farm already owns (map tile or kit). */
    try { if (root._lfgGreenhouse && root._lfgGreenhouse.syncOwnership) root._lfgGreenhouse.syncOwnership('gllf-startup'); else if (root._lfgGreenhouse) root._lfgGreenhouse.hasGreenhouse(); } catch (e) {}
    if (started.length) root.setAttribute('data-gllf-primed', started.join(' '));
    restoreViewIds(root); repairSoil(root); watchGuide(root);
    setTimeout(function () { restoreViewIds(root); }, 2500);
  }

  function whenReady(root) {
    var done = false;
    function go() { if (done) return; done = true; setTimeout(function () { prime(root); }, 50); }
    if (root._lfg && root.dataset.bundleShared === 'ready') return go();
    root.addEventListener('lfg:bundle-ready', function (e) { if (e.detail && e.detail.name === 'shared') go(); });
    /* fallback: poll briefly for the shared bundle */
    var n = 0, t = setInterval(function () { if (root._lfg && root.dataset.bundleShared === 'ready') { clearInterval(t); go(); } else if (++n > 120) clearInterval(t); }, 250);
  }

  var tries = 0;
  (function wait() {
    var root = document.getElementById('lfg-simulator-root');
    if (!root && document.readyState === 'complete') return;
    if (root && root._lfg) return whenReady(root);
    if (++tries < 240) setTimeout(wait, 250);
  })();
})();
